<template>
  <div class="service-list">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">{{ $t('services.title') }}</h2>
      <b-button variant="primary" @click="createService">
        <i class="fas fa-plus mr-2"></i>{{ $t('services.create_button') }}
      </b-button>
    </div>

    <CrudTable
      :columns="fields"
      :data-sources="services"
      :pagination="paginationInfo"
      :loader="isLoading"
      list-url="/services"
      @search="handleSearch"
      @filter="handleFilter"
      @refresh="fetchServices"
    >
      <!-- Title Column -->
      <template #cell(title)="data">
        <div class="d-flex align-items-center">
          <div>
            <div class="font-weight-bold">{{ data.item.title || 'N/A' }}</div>
            <div class="text-muted small">{{ data.item.sort_description || 'N/A' }}</div>
          </div>
        </div>
      </template>

      <!-- Price Column -->
      <template #cell(price)="data">
        <div>
          {{ formatPrice(data.item.price) }}
        </div>
      </template>

      <!-- Status Column -->
      <template #cell(status)="data">
        <div>
          {{ data.item.status ? 'Active' : 'Inactive' }}
        </div>
      </template>

      <!-- Actions Column -->
      <template #cell(actions)="data">
        <div class="d-flex gap-2">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="editService(data.item)"
          >
            <i class="fas fa-edit"></i>
          </b-button>
          <b-button
            variant="outline-danger"
            size="sm"
            @click="confirmDelete(data.item)"
          >
            <i class="fas fa-trash"></i>
          </b-button>
        </div>
      </template>
    </CrudTable>

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteModal"
      :title="$t('services.delete_modal_title')"
      @ok="handleDelete"
      ok-variant="danger"
    >
      {{ $t('services.delete_modal_message') }}
    </b-modal>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import CrudTable from "@/components/CrudTable.vue";

export default {
  name: 'ServiceList',
  components: {
    CrudTable
  },

  data() {
    return {
      services: [],
      isLoading: false,
      showDeleteModal: false,
      selectedService: null,
      currentPage: 1,
      perPage: 10,
      sortBy: 'created_at',
      sortDesc: true,

      filters: {
        search: '',
        status: null
      },
      fields: [
        { key: 'title', label: this.$t('services.table_header_title'), sortable: true, sortDirection: 'asc' },
        { key: 'price', label: this.$t('services.table_header_price'), sortable: true, sortDirection: 'asc' },
        { key: 'status', label: this.$t('services.table_header_status'), sortable: true, sortDirection: 'asc' },
        { key: 'actions', label: this.$t('common.actions') }
      ],
      statusOptions: [
        { value: null, text: this.$t('services.status_all') },
        { value: 1, text: this.$t('services.status_active') },
        { value: 0, text: this.$t('services.status_inactive') }
      ],
      paginationInfo: {
        from: 0,
        to: 0,
        total: 0
      }
    }
  },

  methods: {
    formatPrice(price) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(price);
    },

    handleSearch(search) {
      this.filters.search = search;
      this.currentPage = 1;
      this.fetchServices();
    },

    handleFilter(filter) {
      this.filters = { ...this.filters, ...filter };
      this.currentPage = 1;
      this.fetchServices();
    },

    createService() {
      this.$router.push('/admin/services/create');
    },

    editService(service) {
      this.$router.push(`/admin/services/${service.id}/edit`);
    },

    confirmDelete(service) {
      this.selectedService = service;
      this.showDeleteModal = true;
    },

    async handleDelete() {
      try {
        await request({
          method: 'delete',
          url: `/services/${this.selectedService.id}`
        });
        this.fetchServices();
      } catch (error) {
        console.error('Error deleting service:', error);
      }
    },

    async fetchServices() {
      try {
        this.isLoading = true;
        const response = await request({
          method: 'get',
          url: '/services',
          params: {
            page: this.currentPage,
            per_page: this.perPage,
            sort_by: this.sortBy,
            sort_desc: this.sortDesc,
            ...this.filters
          }
        });

        if (response.data && response.data.data) {
          this.services = response.data.data;
          if (response.data.meta) {
            this.paginationInfo = {
              from: response.data.meta.from || 0,
              to: response.data.meta.to || 0,
              total: response.data.meta.total || 0
            };
          }
        } else {
          this.services = [];
          this.paginationInfo = {
            from: 0,
            to: 0,
            total: 0
          };
        }
      } catch (error) {
        console.error('Error fetching services:', error);
        this.services = [];
        this.paginationInfo = {
          from: 0,
          to: 0,
          total: 0
        };
      } finally {
        this.isLoading = false;
      }
    }
  },

  created() {
    this.fetchServices();
  }
}
</script>

<style scoped>
.service-list {
  padding: 20px;
}
</style> 